import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';

/**
 * ============================================================================
 * TMA ICON COMPONENT
 * ============================================================================
 * Wraps mat-icon with automatic dual-state (outline/solid) switching.
 *
 * USAGE:
 *   <tma-icon name="patient"></tma-icon>
 *   <tma-icon name="physician" [solid]="isNavActive" size="5"></tma-icon>
 *   <tma-icon name="tminsights" size="8" class="text-blue-500"></tma-icon>
 *   <tma-icon name="tmai" [solid]="true" size="6"></tma-icon>
 *
 * SIZE INPUT maps to Fuse icon-size-N Tailwind classes:
 *   4 → 16px, 5 → 20px (default), 6 → 24px, 8 → 32px, 10 → 40px
 *
 * COLOR: Inherits currentColor — set via Tailwind text-* classes on parent
 *   or directly: class="text-primary" / class="text-blue-600"
 * ============================================================================
 */
@Component({
  selector: 'tma-icon',
  standalone: true,
  imports: [MatIconModule, NgClass],
  template: `
    <mat-icon
      [svgIcon]="resolvedIcon"
      [ngClass]="sizeClass"
    ></mat-icon>
  `,
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    mat-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TmaIconComponent implements OnChanges {
  /** Icon name from TMA_ICON_NAMES — e.g. 'patient', 'physician', 'tmai' */
  @Input({ required: true }) name!: string;

  /** When true, uses the solid/filled variant (active nav state) */
  @Input() solid = false;

  /** Icon size. Maps to Fuse's icon-size-N Tailwind classes. Default: 5 (20px) */
  @Input() size: 4 | 5 | 6 | 7 | 8 | 10 = 5;

  resolvedIcon = '';
  sizeClass = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['name'] || changes['solid']) {
      this.resolvedIcon = this.solid
        ? `tma:${this.name}-solid`
        : `tma:${this.name}`;
    }
    if (changes['size']) {
      this.sizeClass = `icon-size-${this.size}`;
    }
  }
}
